import type { Timestamp } from 'firebase/firestore'
import { z } from 'zod'

export const createPostSchema = z.object({
  title: z.string().min(1, 'Title is required').max(150, 'Title must be 150 characters or less'),
  subtitle: z.string().max(200, 'Subtitle must be 200 characters or less').optional(),
  content: z.string().min(1, 'Content is required').max(50000, 'Content must be 50,000 characters or less'),
  coverImage: z.string().optional(),
  status: z.enum(['draft', 'published', 'archived']),
  visibility: z.enum(['media_only', 'fans_only', 'both_media_and_fans']),
})

export const firestorePostSchema = z.object({
  id: z.string().optional(),
  title: z.string(),
  subtitle: z.string().optional(),
  content: z.string(),
  description: z.string(),
  authorId: z.string(),
  authorName: z.string(),
  authorPicUrl: z.string().optional(),
  imageUrl: z.string().optional(),
  status: z.enum(['draft', 'published', 'archived']),
  visibility: z.enum(['media_only', 'fans_only', 'both_media_and_fans']),
  companyId: z.string(),
  createdAt: z.custom<Timestamp>().optional(),
  updatedAt: z.custom<Timestamp>().optional(),
  attachments: z
    .array(
      z.object({
        type: z.enum(['audio', 'document', 'image']),
        url: z.string(),
        fileName: z.string().optional(),
        fileSize: z.number().optional(),
      }),
    )
    .optional(),
})

export type CreatePostFormData = z.infer<typeof createPostSchema>
export type FirestorePost = z.infer<typeof firestorePostSchema>

export interface PostAttachment {
  type: 'audio' | 'document' | 'image'
  url: string
  fileName?: string
  fileSize?: number
}

export interface Post {
  id?: string
  title: string
  subtitle?: string
  description: string
  content?: string
  authorId?: string
  authorName: string
  authorPicUrl?: string
  imageUrl?: string
  companyId: string
  status: 'draft' | 'published' | 'archived'
  visibility?: 'media_only' | 'fans_only' | 'both_media_and_fans'
  createdAt?: Timestamp
  updatedAt?: Timestamp
  attachments?: PostAttachment[]
}

export type PostInput = Omit<Post, 'id' | 'createdAt' | 'updatedAt'>
